import { AppImage } from '@/components/app/AppImage';
import { FormErrorDisplay } from '@/components/common/FormErrorDisplay';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useApi } from '@/hooks/useApi';
import { useAppContext } from '@/hooks/useAppContext';
import { useTranslation } from '@/hooks/useTranslation';
import i18n from '@/i18n';
import { useCommonStore } from '@/stores/common';
import { ErrorBag } from '@/types/error';
import { parseAxiosError } from '@/utils/error';
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

function RegistrationScreen() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { appType } = useAppContext();
  const setFromIntegration = useCommonStore((state) => state.setFromIntegration);
  const fromIntegration = useCommonStore((state) => state.fromIntegration);
  const integrationData = useCommonStore((state) => state.integrationData);
  const [ errors, setErrors ] = useState<ErrorBag>({});

  const { sendRequest } = useApi({
    url: '/register',
    method: 'POST',
  });

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [password1, setPassword1] = useState('');
  const [organizationName, setOrganizationName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  
  const isRegistrationEnabled = appType === 'viber-uzenetek' || window.location.host.includes('localhost');

  const isDisabled =
    !name ||
    !organizationName ||
    !email ||
    !password ||
    (!password1 && password.length > 7 && password !== password1) ||
    !contactPhone ||
    !termsAccepted;


  const handleSubmit = useCallback(async () => {
    if (password !== password1) {
      toast.error(t('passwordsDoNotMatch'));
      return;
    }

    try {
      const { data, error } = await sendRequest({
        name,
        email,
        password,
        password_confirmation: password1,
        organization_name: organizationName,
        language: i18n.language,
        actiUrl: `${window.location.origin}/verify?token=`,
        integration: fromIntegration,
        integration_data: integrationData?.[fromIntegration ?? ''] ?? {},
        contact_phone: contactPhone,
      });

      if (error) {
        toast.error(t('registrationFailed'));

        const parsedErrors = parseAxiosError(error);
        if (parsedErrors) {
          setErrors(parsedErrors.errors);
        }

        return;
      }

      if (data) {
        toast.success(t('registrationSuccess'));

        setFromIntegration(null);
        navigate('/registration-success');
      }

    } catch (error) {
      toast.error(t('registrationFailed'));
    }
  }, [
    t, sendRequest, name, email, password, password1, organizationName,
    fromIntegration, integrationData, navigate, setFromIntegration,
    contactPhone,
  ]);

  useEffect(() => {
    if (!isRegistrationEnabled) {
      navigate('/login');
    }
  }, [isRegistrationEnabled, navigate]);

  if (!isRegistrationEnabled) {
    return null;
  }

  return (
    <div className="flex flex-col items-center justify-center w-screen h-screen">
      <div className="w-full max-w-2xl">
        <Card className="shadow-lg">
          <Link to="/">
            <AppImage className="w-32 md:w-44 max-h-16 md:max-h-24 mx-auto mt-6" />
          </Link>
          <CardHeader>
            <CardTitle className="text-xl">{t('signUp')}</CardTitle>
            <CardDescription>
              {t('enterYourInformationToCreateAnAccount')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4">
              <div className="grid gap-2">
                <Label htmlFor="name">{t('name')}</Label>
                <Input
                  id="first-name"
                  placeholder="Name"
                  required
                  onChange={(e) => setName(e.target.value)}
                />
                <FormErrorDisplay error={errors.name} field="name" />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="email">{t('email')}</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="email@example.com"
                  required
                  onChange={(e) => setEmail(e.target.value)}
                />
                <FormErrorDisplay error={errors.email} field="email" />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="password">{t('password')}</Label>
                <Input
                  id="password"
                  type="password"
                  onChange={(e) => setPassword(e.target.value)}
                />
                <FormErrorDisplay error={errors.password} field="password" />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="password1">{t('passwordConfirmation')}</Label>
                <Input
                  id="password1"
                  type="password"
                  onChange={(e) => setPassword1(e.target.value)}
                />
                <FormErrorDisplay error={errors.password_confirmation} field="password_confirmation" />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="organizationName">{t('organizationName')}</Label>
                <Input
                  id="organizationName"
                  onChange={(e) => setOrganizationName(e.target.value)}
                />
                <FormErrorDisplay error={errors.organization_name} field="organization_name" />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="contactPhone">{t('contactPhone')}</Label>
                <Input
                  id="contactPhone"
                  onChange={(e) => setContactPhone(e.target.value)}
                  required
                />
                <FormErrorDisplay error={errors.contact_phone} field="organization_name" />
              </div>
              <div className="flex items-center gap-2">
                <Checkbox id="terms-checkbox" onCheckedChange={state => setTermsAccepted(state !== "indeterminate" ? state : false)} />
                <Label htmlFor="terms-checkbox">
                  {t("agreeToThe")}{" "}
                  <a className="underline" href="https://viberuzenetek.hu/docs/Viberuzenetek-Felhasznalas.pdf" target="_blank">
                    {t('termsOfService2')}
                  </a>
                </Label>
              </div>
              <Button
                type="submit"
                className="w-full"
                disabled={isDisabled}
                onClick={handleSubmit}
                variant="conversionCraft"
              >
                {t('createAnAccount')}
              </Button>
            </div>
            <div className="flex items-center justify-center mt-4 text-center text-sm gap-1">
              {t('alreadyHaveAnAccount')}
              <Link to="/login" className="underline">
                {t('signIn')}
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export const Component = RegistrationScreen;
